import React from 'react';
import { useHistory } from 'react-router-dom';
import { resolveLeadOwnerName } from '../utils/leadOwner';
import { leadPath } from '../utils/crmEnv';

const statusLabels = {
    new: 'New',
    contacted: 'Contacted',
    qualified: 'Qualified',
    unqualified: 'Unqualified',
    converted: 'Converted',
};

const sourceLabels = {
    website: 'Website',
    referral: 'Referral',
    cold_call: 'Cold Call',
    advertisement: 'Advertisement',
    social_media: 'Social Media',
    walk_in: 'Walk-in',
    other: 'Other',
};

const formatDateTime = (dateString) => {
    if (!dateString) return 'None';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'None';
    return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const formatRelativeTime = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'Unknown';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const getFollowUpStatus = (lead) => {
    const now = new Date();
    const due = lead?.nextFollowUpAt ? new Date(lead.nextFollowUpAt) : null;
    const openCount = Number(lead?.openCount || 0);
    const overdueCount = Number(lead?.overdueCount || 0);

    if (due && !Number.isNaN(due.getTime())) {
        if (due.getTime() < now.getTime()) return { label: 'Overdue', className: 'crm-pill-danger', display: formatDateTime(lead.nextFollowUpAt) };
        return { label: 'Coming up', className: 'crm-pill-warning', display: formatDateTime(lead.nextFollowUpAt) };
    }
    if (overdueCount > 0) return { label: 'Overdue', className: 'crm-pill-danger', display: '-' };
    if (openCount === 0 && overdueCount === 0) {
        return { label: 'Completed', className: 'crm-pill-success', display: 'Everything up to date' };
    }
    return { label: 'Coming up', className: 'crm-pill-warning', display: '-' };
};

const formatStage = (value) => {
    if (!value) return 'None';
    return String(value).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const getLastTouchLabel = (lead) => {
    if (!lead?.lastTouchType) return 'Activity';
    return String(lead.lastTouchType).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatLocation = (lead) => {
    if (lead?.locationSummary) return lead.locationSummary;
    const localities = Array.isArray(lead?.localities)
        ? lead.localities
        : typeof lead?.localities === 'string'
            ? lead.localities.split(',').map((item) => item.trim()).filter(Boolean)
            : typeof lead?.locality === 'string'
                ? lead.locality.split(',').map((item) => item.trim()).filter(Boolean)
                : [];
    const locality = localities[0] || '';
    const city = lead?.city || '';
    const pincode = lead?.pincode || '';
    const segments = [];
    if (locality) segments.push(locality);
    if (city) segments.push(city);
    if (pincode) segments.push(pincode);
    return segments.length > 0 ? segments.join(', ') : 'No location';
};

const clampScore = (score) => Math.max(0, Math.min(100, Number(score) || 0));

function LeadCard({ lead, currentUser, onEdit, onDelete }) {
    const history = useHistory();

    const displayName = lead.name || `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || 'Unnamed Lead';
    const followUpStatus = getFollowUpStatus(lead);
    const score = clampScore(lead.score || 0);
    const phone = lead.phone || lead.mobile;
    const whatsapp = phone ? phone.replace(/[^0-9]/g, '') : '';
    const ownerName = resolveLeadOwnerName(lead, currentUser);
    const lastTouchSource = lead.lastTouchAt || lead.lastContactedAt || lead.updatedAt || lead.createdAt;
    const intent = lead.intentSummary || 'Unknown';
    const location = formatLocation(lead);
    const propertyTypes = Array.isArray(lead.propertyTypes) ? lead.propertyTypes : [];
    const dealsSummary = lead.openDealsCount
        ? `${lead.openDealsCount} open | ${formatStage(lead.latestDealStage)}`
        : 'No open deals';
    const leadShortId = lead.shortId ? String(lead.shortId).toUpperCase() : '-';

    const handleNavigate = () => {
        history.push(leadPath(lead.id));
    };

    const handleEdit = (event) => {
        event.stopPropagation();
        onEdit?.(lead);
    };

    const handleDelete = (event) => {
        event.stopPropagation();
        onDelete?.(lead.id);
    };

    return (
        <div className="crm-card crm-card-hover crm-lead-card" onClick={handleNavigate} role="button" tabIndex={0}>
            <div className="crm-card-body">
                <div className="crm-lead-card-header">
                    <div>
                        <div className="crm-lead-title">{displayName}</div>
                        <div className="crm-lead-subtitle">{lead.company || '?'}</div>
                    </div>
                    <span className={`crm-badge crm-badge-${lead.status || 'new'}`}>
                        {statusLabels[lead.status] || 'New'}
                    </span>
                </div>

                <div className="crm-lead-badges">
                    <span className="crm-badge crm-badge-source">
                        {sourceLabels[lead.source] || 'Other'}
                    </span>
                    <span className="crm-badge crm-badge-intent">{intent}</span>
                </div>

                <div className="crm-lead-section">
                    <div className="crm-meta-label">Lead ID</div>
                    <div className="crm-meta-value">{leadShortId}</div>
                </div>

                <div className="crm-lead-section">
                    <div className="crm-meta-label">Next follow-up</div>
                    <div className="crm-lead-inline">
                        <span className="crm-meta-value">{followUpStatus.display ?? formatDateTime(lead.nextFollowUpAt)}</span>
                        <span className={`crm-pill ${followUpStatus.className}`}>{followUpStatus.label}</span>
                    </div>
                </div>

                <div className="crm-lead-section">
                    <div className="crm-meta-label">Last touch</div>
                    <div className="crm-meta-value">
                        {getLastTouchLabel(lead)} - {formatRelativeTime(lastTouchSource)}
                    </div>
                </div>

                <div className="crm-lead-section">
                    <div className="crm-meta-label">Location</div>
                    <div className="crm-meta-value">{location}</div>
                </div>

                <div className="crm-lead-section">
                    <div className="crm-meta-label">Intent</div>
                    <div className="crm-meta-value">{intent || 'Unknown'}</div>
                </div>

                <div className="crm-lead-section">
                    <div className="crm-meta-label">Deals</div>
                    <div className="crm-meta-value">{dealsSummary}</div>
                </div>

                <div className="crm-lead-meta">
                    <div>
                        <div className="crm-meta-label">Assigned To</div>
                        <div className="crm-meta-value">{ownerName}</div>
                    </div>
                    <div className="crm-score-row">
                        <span className="crm-score-label">Score</span>
                        <div className="crm-score-bar">
                            <span style={{ width: `${score}%` }} />
                        </div>
                        <span className="crm-score-value">{score}</span>
                    </div>
                </div>

                <div className="crm-lead-card-footer" onClick={(event) => event.stopPropagation()}>
                    <div className="crm-quick-actions crm-quick-actions-grid">
                        {phone ? (
                            <a className="crm-icon-btn" href={`tel:${phone}`} title="Call">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                                </svg>
                            </a>
                        ) : (
                            <button className="crm-icon-btn" type="button" title="Call" disabled>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                                </svg>
                            </button>
                        )}
                        {whatsapp ? (
                            <a className="crm-icon-btn" href={`https://wa.me/${whatsapp}`} title="WhatsApp" target="_blank" rel="noreferrer">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 11.5a8.5 8.5 0 1 1-3.33-6.72" />
                                    <path d="M22 12c0 4.97-4.03 9-9 9a8.96 8.96 0 0 1-4.65-1.3L3 21l1.32-4.05A8.96 8.96 0 0 1 3 12c0-4.97 4.03-9 9-9" />
                                </svg>
                            </a>
                        ) : (
                            <button className="crm-icon-btn" type="button" title="WhatsApp" disabled>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 11.5a8.5 8.5 0 1 1-3.33-6.72" />
                                    <path d="M22 12c0 4.97-4.03 9-9 9a8.96 8.96 0 0 1-4.65-1.3L3 21l1.32-4.05A8.96 8.96 0 0 1 3 12c0-4.97 4.03-9 9-9" />
                                </svg>
                            </button>
                        )}
                        {lead.email ? (
                            <a className="crm-icon-btn" href={`mailto:${lead.email}`} title="Email">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                    <polyline points="22,6 12,13 2,6" />
                                </svg>
                            </a>
                        ) : (
                            <button className="crm-icon-btn" type="button" title="Email" disabled>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                    <polyline points="22,6 12,13 2,6" />
                                </svg>
                            </button>
                        )}
                    </div>
                    <div className="crm-quick-actions crm-quick-actions-grid">
                        <button className="crm-icon-btn" type="button" title="Edit" onClick={handleEdit}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 20h9" />
                                <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                            </svg>
                        </button>
                        <button className="crm-icon-btn" type="button" title="Delete" onClick={handleDelete}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                <path d="M10 11v6" />
                                <path d="M14 11v6" />
                                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default LeadCard;
